import SEO from '../components/SEO';
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Filter, SlidersHorizontal, Lock, Bell, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import StakingCard from '../components/staking/StakingCard';
import { StakingOption } from '../types';
import { useAuthStore } from '../store/authStore';

const CATEGORIES = ['All Categories','Layer 1','Layer 2','Liquid Staking','DeFi Lending','Stablecoins','Real World Assets','Gaming','Cross-Chain','Derivatives','NFT','Infrastructure','Privacy','AI & Data','Social','DeFi','Cosmos','Polkadot'];

export default function StakingPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [riskFilter, setRiskFilter] = useState('all');
  const [sortBy, setSortBy] = useState<'apy' | 'name'>('name');
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 12;
  const { isAuthenticated } = useAuthStore();

  const { data, isLoading } = useQuery({
    queryKey: ['staking-options'],
    queryFn: async () => {
      const response = await api.get('/staking/options?limit=200');
      return response.data.data;
    },
  });

  const options: StakingOption[] = data?.options || [];

  const filteredOptions = useMemo(() => {
    let result = [...options];
    if (selectedCategory !== 'All Categories') result = result.filter(o => o.category === selectedCategory);
    if (riskFilter !== 'all') result = result.filter(o => o.riskLevel === riskFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(o => o.name.toLowerCase().includes(q) || o.symbol.toLowerCase().includes(q) || o.category.toLowerCase().includes(q));
    }
    if (sortBy === 'apy') result.sort((a, b) => Number(b.apy) - Number(a.apy));
    else result.sort((a, b) => a.name.localeCompare(b.name));
    return result;
  }, [options, selectedCategory, riskFilter, searchQuery, sortBy]);

  const totalPages = Math.ceil(filteredOptions.length / ITEMS_PER_PAGE);
  const paginatedOptions = filteredOptions.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const hasFilters = selectedCategory !== 'All Categories' || riskFilter !== 'all' || searchQuery.trim();

  const clearFilters = () => { setSelectedCategory('All Categories'); setRiskFilter('all'); setSearchQuery(''); };

  return (
    <div className="min-h-screen bg-navy-dark py-12">
      <SEO title="Crypto Staking Comparison — 156+ Protocols" description="Compare live staking APY rates across 156+ crypto protocols. Side-by-side staking comparison for Ethereum, Cosmos ATOM, Solana, Cardano, and more. Updated in real time." canonical="https://www.residualvault.com/staking" keywords="staking comparison, crypto staking rates, best staking APY 2026, ethereum staking, ATOM staking, SOL staking, staking rewards comparison" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {!isAuthenticated && (
          <div className="mb-8 p-6 bg-gradient-to-r from-neon-red/10 via-electric-blue/10 to-neon-red/10 border border-neon-red/30 rounded-2xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-cream mb-1">Track APY changes and never miss a rate move</h2>
                <p className="text-sky-blue text-sm">Create a free account to set APY alerts, compare unlimited protocols, and see full rate history.</p>
              </div>
              <Link to="/register" className="btn-neon whitespace-nowrap flex items-center gap-2">
                <Bell className="w-4 h-4" /> Sign up free
              </Link>
            </div>
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2"><span className="text-cream">Staking </span><span className="gradient-text-neon">Options</span></h1>
          <p className="text-sky-blue">{isLoading ? 'Loading...' : `${options.length} options across ${CATEGORIES.length - 1} categories`}</p>
        </div>
        <div className="card-premium mb-8">
          <div className="flex items-center gap-2 mb-4"><SlidersHorizontal className="w-5 h-5 text-neon-red" /><h2 className="text-lg font-semibold text-cream">Filters</h2></div>
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-56 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-blue" />
              <input type="text" placeholder="Search by name, symbol, category..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-navy-dark border border-sky-blue/30 rounded-lg text-cream placeholder-sky-blue/50 focus:outline-none focus:border-neon-red transition-colors" />
            </div>
            <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="px-4 py-2.5 bg-navy-dark border border-sky-blue/30 rounded-lg text-cream focus:outline-none focus:border-neon-red transition-colors">{CATEGORIES.map(c => <option key={c} value={c} className="bg-navy-dark">{c}</option>)}</select>
            <select value={riskFilter} onChange={e => setRiskFilter(e.target.value)} className="px-4 py-2.5 bg-navy-dark border border-sky-blue/30 rounded-lg text-cream focus:outline-none focus:border-neon-red transition-colors">
              <option value="all" className="bg-navy-dark">All Risk Levels</option>
              <option value="low" className="bg-navy-dark">Low Risk</option>
              <option value="medium" className="bg-navy-dark">Medium Risk</option>
              <option value="high" className="bg-navy-dark">High Risk</option>
            </select>
            <select value={sortBy} onChange={e => setSortBy(e.target.value as 'apy' | 'name')} className="px-4 py-2.5 bg-navy-dark border border-sky-blue/30 rounded-lg text-cream focus:outline-none focus:border-neon-red transition-colors">
              <option value="apy" className="bg-navy-dark">Highest APY</option>
              <option value="name" className="bg-navy-dark">Alphabetical</option>
            </select>
          </div>
          {hasFilters && (
            <div className="mt-4 flex items-center gap-3 text-sm pt-4 border-t border-sky-blue/20">
              <span className="text-sky-blue">Showing <strong className="text-cream">{filteredOptions.length}</strong> of <strong className="text-cream">{options.length}</strong> options</span>
              <button onClick={clearFilters} className="text-neon-red hover:text-accent-red underline transition-colors">Clear all filters</button>
            </div>
          )}
        </div>
        {isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{[...Array(9)].map((_, i) => (<div key={i} className="card-premium h-96 animate-pulse"><div className="h-12 bg-steel-blue/20 rounded mb-4"></div><div className="h-4 bg-steel-blue/20 rounded mb-2"></div><div className="h-4 bg-steel-blue/20 rounded w-2/3"></div></div>))}</div>
        ) : filteredOptions.length === 0 ? (
          <div className="card-premium text-center py-16"><Filter className="w-16 h-16 text-sky-blue/30 mx-auto mb-4" /><p className="text-sky-blue text-lg mb-4">No options match your filters.</p><button onClick={clearFilters} className="btn-neon">Clear filters</button></div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedOptions.map((option) => (
                <StakingCard key={option.id} option={option} />
              ))}
            </div>

            {!isAuthenticated && page === 1 && (
              <div className="mt-8 card-premium text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <TrendingUp className="w-5 h-5 text-neon-red" />
                  <h3 className="text-xl font-bold text-cream">Get more from ResidualVault</h3>
                </div>
                <p className="text-sky-blue mb-4 max-w-lg mx-auto">
                  Free accounts get APY alerts, full rate history, and unlimited protocol comparisons. No credit card required.
                </p>
                <div className="flex gap-3 justify-center flex-wrap">
                  <Link to="/register" className="btn-neon inline-flex items-center gap-2">Create free account</Link>
                  <Link to="/pricing" className="bg-steel-blue/20 border border-sky-blue/30 text-sky-blue font-semibold px-6 py-3 rounded-lg hover:bg-steel-blue/30 transition-all inline-flex items-center gap-2">
                    <Lock className="w-4 h-4" /> View plans from $3.69/mo
                  </Link>
                </div>
              </div>
            )}
          </>
        )}
        {totalPages > 1 && (
          <div className="mt-12 flex justify-center items-center gap-4">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-6 py-2.5 rounded-lg border border-sky-blue/30 text-cream disabled:opacity-30 disabled:cursor-not-allowed hover:bg-sky-blue/10 transition-all">Previous</button>
            <span className="px-4 py-2 text-cream">Page <strong className="text-neon-red">{page}</strong> of <strong>{totalPages}</strong></span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-6 py-2.5 rounded-lg border border-sky-blue/30 text-cream disabled:opacity-30 disabled:cursor-not-allowed hover:bg-sky-blue/10 transition-all">Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
